import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from '../lib/queryClient';
import { useToast } from './use-toast';

// Helper to map backend invoice fields to frontend camelCase
function mapInvoiceFields(invoice: any) {
  if (!invoice) return invoice;
  return {
    ...invoice,
    amountPaid: invoice.amount_paid,
    total: invoice.total,
    // Add more mappings as needed
  };
}

export function useInvoices() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Obtener todas las facturas
  const {
    data: invoicesRaw,
    isLoading: isLoadingInvoices,
    error: invoicesError,
    refetch: refetchInvoices
  } = useQuery({
    queryKey: ["/api/protected/invoices"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/protected/invoices");
      const data = await res.json();
      return Array.isArray(data) ? data.map(mapInvoiceFields) : [];
    }
  });
  const invoices = invoicesRaw;

  // Get an invoice by ID
  const getInvoice = (id: number) => {
    return useQuery({
      queryKey: ["/api/protected/invoices", id],
      queryFn: async () => {
        const res = await apiRequest("GET", `/api/protected/invoices?id=${id}`);
        return await res.json();
      },
      enabled: !!id,
    });
  };

  // Crear una nueva factura
  const createInvoiceMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/protected/invoices", data);
      return await res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/protected/invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/protected/projects"] });
      toast({
        title: "Factura creada",
        description: "La factura ha sido creada correctamente.",
      });
      return data;
    },
    onError: (error: Error) => {
      toast({
        title: "Error al crear factura",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update an invoice
  const updateInvoice = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const res = await apiRequest("PATCH", `/api/protected/invoices?id=${id}`, data);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/protected/invoices"] });
      toast({
        title: "Success",
        description: "Invoice updated successfully",
        variant: "default"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to update invoice: ${error.message}`,
        variant: "destructive"
      });
    }
  });

  // Delete an invoice
  const deleteInvoice = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("DELETE", `/api/protected/invoices?id=${id}`);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/protected/invoices"] });
      toast({
        title: "Success",
        description: "Invoice deleted successfully",
        variant: "default"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to delete invoice: ${error.message}`,
        variant: "destructive"
      });
    }
  });

  // Update invoice status
  const updateInvoiceStatus = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const res = await apiRequest("PATCH", `/api/protected/invoices?id=${id}`, { status });
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/protected/invoices"] });
      toast({
        title: "Success",
        description: "Invoice status updated successfully",
        variant: "default"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to update invoice status: ${error.message}`,
        variant: "destructive"
      });
    }
  });

  // Cancel an invoice
  const cancelInvoice = useMutation({
    mutationFn: async ({ id, notes }: { id: number; notes: string }) => {
      const res = await apiRequest("POST", `/api/protected/invoices?id=${id}&action=cancel`, { notes });
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/protected/invoices"] });
      toast({
        title: "Success",
        description: "Invoice cancelled successfully",
        variant: "default"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to cancel invoice: ${error.message}`,
        variant: "destructive"
      });
    }
  });

  // Record payment
  const recordPayment = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      console.log('🔍 Frontend: Making payment request:', { invoiceId: id, paymentData: data });
      const res = await apiRequest("POST", `/api/protected/invoices?id=${id}&action=payment`, data);
      const result = await res.json();
      console.log('🔍 Frontend: Payment response:', result);
      return result;
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/protected/invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/protected/projects"] });
      
      // Backend auto-create logic should handle project creation
      // Just refresh projects list to show any newly created projects
      console.log('🔍 Frontend: Payment successful, refreshing projects list...');
      
      toast({
        title: "Success",
        description: "Payment recorded successfully",
        variant: "default"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: `Failed to record payment: ${error.message}`,
        variant: "destructive"
      });
    }
  });

  // Reverse payment
  const reversePayment = useMutation({
    mutationFn: async ({ invoiceId, paymentId, amount, reason, referenceNumber }: { invoiceId: number; paymentId?: number; amount?: number; reason?: string; referenceNumber?: string }) => {
      // Backend accepts either { paymentId } to lookup amount, or explicit { amount }
      const body: any = {};
      if (typeof paymentId === 'number') body.paymentId = paymentId;
      if (typeof amount === 'number') body.amount = amount;
      if (reason) body.reason = reason;
      if (referenceNumber) body.referenceNumber = referenceNumber;
      const res = await apiRequest("POST", `/api/protected/invoices?id=${invoiceId}&action=reverse-payment`, body);
      return await res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/protected/invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/protected/invoices", data.invoice?.id] });
      toast({
        title: "Payment refunded",
        description: "The payment has been refunded successfully.",
      });
      return data;
    },
    onError: (error: Error) => {
      toast({
        title: "Error refunding payment",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Obtener facturas por proyecto
  const getInvoicesByProject = (projectId: number) => {
    return useQuery({
      queryKey: ["/api/protected/projects", projectId, "invoices"],
      queryFn: async () => {
        const res = await apiRequest("GET", `/api/protected/projects/${projectId}/invoices`);
        return await res.json();
      },
      enabled: !!projectId,
    });
  };

  return {
    invoices,
    isLoadingInvoices,
    invoicesError,
    refetchInvoices,
    getInvoice,
    createInvoiceMutation,
    updateInvoiceMutation: updateInvoice,
    deleteInvoiceMutation: deleteInvoice,
    updateInvoiceStatusMutation: updateInvoiceStatus,
    cancelInvoiceMutation: cancelInvoice,
    registerPaymentMutation: recordPayment,
    refundPaymentMutation: reversePayment,
    getInvoicesByProject,
  };
}